import React, { useMemo, useState } from 'react';
import { Vessel } from '../../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Ship, Package, Calendar, TrendingUp, Sparkles, Loader2, Truck, Users, UserCircle } from 'lucide-react';
import { analyzeData } from '../../services/geminiService';

interface DashboardProps {
  vessels: Vessel[];
}

const PORT_CHART_COLORS = ['#0ea5e9', '#0284c7', '#0369a1', '#bae6fd', '#7dd3fc', '#38bdf8'];

// Helper to unify grade display by removing 'Bitumen', 'Grade', etc.
const normalizeGrade = (grade: string) => {
    if (!grade) return 'UNKNOWN';
    return grade.toUpperCase()
        .replace(/BITUMEN/g, '')
        .replace(/GRADE/g, '')
        .replace(/FRADE/g, '')
        .trim();
};

export const Dashboard: React.FC<DashboardProps> = ({ vessels }) => {
  const [aiInsight, setAiInsight] = useState<string | null>(null);
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [chartToggleMode, setChartToggleMode] = useState<'IMPORTER' | 'SUPPLIER'>('IMPORTER');

  // Computed Stats
  const stats = useMemo(() => {
    const totalVessels = vessels.length;
    let totalVolume = 0;
    const gradeCounts: Record<string, number> = {};
    const importerCounts: Record<string, number> = {};
    const supplierCounts: Record<string, number> = {};

    vessels.forEach(v => {
      // Defensive check for manifest
      if (v.manifest && Array.isArray(v.manifest)) {
        v.manifest.forEach(item => {
          totalVolume += (item.quantity || 0);
          
          // Grade aggregation with normalization
          const g = normalizeGrade(item.bitumenGrade || 'Unknown');
          gradeCounts[g] = (gradeCounts[g] || 0) + (item.quantity || 0);

          // Importer aggregation
          const imp = item.importerName || 'Unknown';
          importerCounts[imp] = (importerCounts[imp] || 0) + (item.quantity || 0);

          // Supplier aggregation
          const sup = item.supplier || 'Unknown';
          supplierCounts[sup] = (supplierCounts[sup] || 0) + (item.quantity || 0);
        });
      }
    });

    const topImporter = Object.entries(importerCounts).sort((a, b) => (b[1] as number) - (a[1] as number))[0]?.[0] || 'N/A';
    const topSupplier = Object.entries(supplierCounts).sort((a, b) => (b[1] as number) - (a[1] as number))[0]?.[0] || 'N/A';

    return { totalVessels, totalVolume, topImporter, topSupplier, gradeCounts, importerCounts, supplierCounts };
  }, [vessels]);

  const gradeData = Object.entries(stats.gradeCounts).map(([name, value]) => ({ name, value }));
  
  const barChartData = useMemo(() => {
    const counts = chartToggleMode === 'IMPORTER' ? stats.importerCounts : stats.supplierCounts;
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value: value as number }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5); // Top 5
  }, [chartToggleMode, stats]);

  const handleGenerateInsight = async () => {
    setLoadingInsight(true);
    const summary = `
      Total Vessels: ${stats.totalVessels}
      Total Volume: ${stats.totalVolume} MT
      Top Importer: ${stats.topImporter}
      Top Supplier: ${stats.topSupplier}
      Grade Breakdown: ${JSON.stringify(stats.gradeCounts)}
      Importer Breakdown: ${JSON.stringify(stats.importerCounts)}
    `;
    
    try {
        const insight = await analyzeData(summary);
        setAiInsight(insight);
    } catch (e) {
        setAiInsight("AI Analysis service unavailable.");
    } finally {
        setLoadingInsight(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center space-x-4">
          <div className="p-3 bg-indigo-50 rounded-lg text-indigo-600">
            <Ship size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Vessels</p>
            <h3 className="text-2xl font-bold text-gray-900">{stats.totalVessels}</h3>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center space-x-4">
          <div className="p-3 bg-indigo-50 rounded-lg text-indigo-600">
            <Package size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Volume</p>
            <h3 className="text-2xl font-bold text-gray-900">{stats.totalVolume.toLocaleString()} MT</h3>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center space-x-4">
          <div className="p-3 bg-orange-100 rounded-lg text-orange-600">
            <Truck size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Top Supplier</p>
            <h3 className="text-lg font-bold text-gray-900 truncate max-w-[150px]" title={stats.topSupplier}>{stats.topSupplier}</h3>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center space-x-4">
          <div className="p-3 bg-green-100 rounded-lg text-green-600">
            <TrendingUp size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Top Importer</p>
            <h3 className="text-lg font-bold text-gray-900 truncate max-w-[150px]" title={stats.topImporter}>{stats.topImporter}</h3>
          </div>
        </div>
      </div>

      {/* AI Insight Section */}
      <div className="bg-slate-900 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Sparkles className="text-indigo-400" />
              <h2 className="text-xl font-semibold">Gemini Port Logistics Analysis</h2>
            </div>
            <button 
              onClick={handleGenerateInsight}
              disabled={loadingInsight}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors flex items-center"
            >
              {loadingInsight ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
              {aiInsight ? 'Refresh Analysis' : 'Generate Insights'}
            </button>
          </div>
          
          {aiInsight ? (
            <div className="bg-white/5 p-4 rounded-lg text-slate-100 leading-relaxed whitespace-pre-line border border-white/10 italic">
              {aiInsight}
            </div>
          ) : (
            <p className="text-slate-400 text-sm">
              Use AI to analyze import trends, detect supply risks, and summarize vessel data instantly.
            </p>
          )}
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-3xl rounded-full -mr-20 -mt-20"></div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Volume by Grade</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={gradeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {gradeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PORT_CHART_COLORS[index % PORT_CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} MT`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Top {chartToggleMode === 'IMPORTER' ? 'Importers' : 'Suppliers'} (Volume)
            </h3>
            <div className="bg-slate-100 p-1 rounded-xl flex border border-slate-200">
              <button 
                onClick={() => setChartToggleMode('IMPORTER')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${chartToggleMode === 'IMPORTER' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <UserCircle size={14} /> Importers
              </button>
              <button 
                onClick={() => setChartToggleMode('SUPPLIER')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${chartToggleMode === 'SUPPLIER' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <Users size={14} /> Suppliers
              </button>
            </div>
          </div>
          
          <div className="h-[300px]">
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{fontSize: 10}} />
                  <YAxis dataKey="name" type="category" width={120} tick={{fontSize: 10}} />
                  <Tooltip formatter={(value) => `${value} MT`} />
                  <Bar 
                    dataKey="value" 
                    fill={chartToggleMode === 'IMPORTER' ? '#6366f1' : '#f59e0b'} 
                    radius={[0, 4, 4, 0]} 
                    barSize={24} 
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-300">
                <TrendingUp size={48} className="opacity-10 mb-2" />
                <p className="text-xs font-bold uppercase tracking-widest">No data available for display</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;